import { ParsedUrlQuery } from 'querystring';

import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';

import {
  cacheControl,
  filterMarkets,
  getCanonicalUrl,
  getHrefLanguages,
} from '@hultafors/shared/helpers';

import {
  datoApi,
  getContentAreaData,
  markets,
} from '@hultafors/hultaforsgroup/api';
import { routes } from '@hultafors/hultaforsgroup/helpers';
import {
  ContentAreaData,
  GlobalFields,
  NewsPageContentFragment,
  NewsPageListingFragment,
  PageProps,
} from '@hultafors/hultaforsgroup/types';

import { prodDomains } from '../../next.domains';

const ArticleList = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.ArticleList,
  ),
);
const ContentArea = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.ContentArea,
  ),
);
const MoreNewsWrapper = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.MoreNewsWrapper,
  ),
);
const NewsLabel = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.NewsLabel,
  ),
);
const Page = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then((module) => module.Page),
);
const SmallHero = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.SmallHero,
  ),
);

interface NewsPageProps extends PageProps<NewsPageContentFragment> {
  allNewsPages: NewsPageListingFragment[];
}

interface NewsPageParams extends ParsedUrlQuery {
  slug: string;
}

interface NewsPagePath {
  params: NewsPageParams;
  locale?: string;
}

const NewsPage: NextPage<NewsPageProps> = ({
  canonical,
  content,
  allNewsPages,
  hrefLanguages,
  globalContent,
  contentAreaData,
}) => {
  return (
    <Page seo={content.seo} canonical={canonical} hrefLanguages={hrefLanguages}>
      <SmallHero
        topLabel={`${content?.publishDate} - ${content?.category}`}
        title={content?.title}
      />
      <ContentArea content={content.contentArea} data={contentAreaData} />
      <NewsLabel label={`${content?.publishDate} - ${content?.category}`} />
      <MoreNewsWrapper
        title={globalContent?.moreNewsHeader}
        CTAText={globalContent?.moreNewsCta}
        CTAUrl={`/${routes.news}`}
      >
        <ArticleList items={allNewsPages} />
      </MoreNewsWrapper>
    </Page>
  );
};

export const getServerSideProps: GetServerSideProps<
  NewsPageProps,
  NewsPageParams
> = async ({ locale, params: { slug }, res }) => {
  try {
    const dato = datoApi(locale);

    const path = `/news/${slug}`;

    const canonical = getCanonicalUrl(
      locale,
      prodDomains,
      'www.hultaforsgroup.com',
      path,
    );

    const {
      data: { newsPage: content, allNewsPages, ...globalFields },
    } = await dato.getNewsPage(slug);
    if (!content || content === null) {
      return {
        notFound: true,
      };
    }

    const contentAreaData: ContentAreaData = await getContentAreaData(
      dato,
      content.contentArea,
    );

    const hrefLanguages = getHrefLanguages(
      null,
      filterMarkets(markets, 'HULTAFORSGROUP'),
      content?.pageLocales,
    );

    res.setHeader('Cache-Control', cacheControl.page);
    const props: NewsPageProps = {
      allNewsPages,
      canonical,
      content,
      contentAreaData,
      hrefLanguages,
      ...(globalFields as unknown as GlobalFields),
    };

    return { props };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};
export default NewsPage;
